import {computed, onScopeDispose, Ref, watch} from "vue";
import {useIntervalFn} from "@vueuse/core";
import debounce from "lodash.debounce";
import {cacheKey} from "@/lib/cache";
import useSwr from "@/core/composables/swr/useSwr";
import {agendaApi} from "@/container";
import {AgendaAppointment} from "@/core/interfaces/AgendaAppointment";
import {AgendaRenewalReminder} from "@/core/interfaces/AgendaRenewalReminder";
import useOrganizationId from "@/core/composables/useOrganizationId";
import useOrganizationSocketEvent from "@/core/composables/useOrganizationSocketEvent";

const REFRESH_INTERVAL_MS = 60 * 1000;

/**
 * Broadcast events of the appointment package. A batch operation fires one per
 * appointment, hence the debounce on the refetch they trigger.
 */
const APPOINTMENT_EVENTS = [
    'appointment-created',
    'appointment-updated',
    'appointment-canceled',
    'appointment-deleted'
];

const LIVE_REFRESH_DEBOUNCE_MS = 300;

/**
 * Cached payload: what the window holds, plus the SWR key it belongs to.
 */
interface AgendaPayload {
    key: string;
    appointments: AgendaAppointment[];
    reminders: AgendaRenewalReminder[];
}

/**
 * Composable to fetch the agenda appointments of the current organization
 * over a date window. The day, week, month and per-collaborator views all
 * reuse it with different ranges.
 *
 * Freshness comes from two sources: the broadcast events of the appointment
 * package, and a one-minute polling tick that covers a dropped socket — the
 * counter leaves the agenda open all day and must never read a stale grid.
 *
 * A customer id narrows the window to that patient's appointments, server-side.
 */
const useAgenda = (range: Ref<{ from: Date; to: Date } | null>, customerId?: Ref<number | null>) => {
    const organizationId = useOrganizationId();

    /**
     * Request params, single source for the SWR key and the fetcher so the
     * two can never drift apart.
     */
    const params = computed(() => {
        if (!range.value) {
            return null;
        }

        return {
            from: range.value.from.toISOString(),
            to: range.value.to.toISOString(),
            ...(customerId?.value ? {customer_id: customerId.value} : {})
        };
    });

    /**
     * The SWR key.
     */
    const key = computed(() => {
        if (!organizationId.value || !params.value) {
            return null;
        }

        return cacheKey(`organizations/${organizationId.value}/agenda`, params.value);
    });

    /**
     * Fetch the agenda appointments from the API, stamped with the key they
     * were requested for.
     */
    const fetchData = async (): Promise<AgendaPayload> => {
        const requestedKey = key.value;

        if (!organizationId.value || !params.value || !requestedKey) {
            return {key: '', appointments: [], reminders: []};
        }

        const agendaWindow = await agendaApi().index(organizationId.value, params.value);

        return {key: requestedKey, ...agendaWindow};
    };

    const {data: payload, error, mutate} = useSwr<AgendaPayload>(
        () => key.value,
        () => fetchData(),
        {refreshInterval: REFRESH_INTERVAL_MS}
    );

    /**
     * Contents of the displayed window, and nothing else.
     *
     * swrv keeps the previous key's data on a key change (it only flips
     * isValidating), and only overwrites it once a cache entry exists for the
     * new key. Rendering that data would put yesterday's appointments under
     * today's header — clickable, and draggable onto the wrong day — so the
     * payload carries the key it was fetched for and anything else reads as
     * "not loaded yet".
     */
    const agendaWindow = computed(() => {
        return payload.value?.key === key.value ? payload.value : undefined;
    });

    const data = computed(() => agendaWindow.value?.appointments);
    const reminders = computed(() => agendaWindow.value?.reminders ?? []);

    const isLoading = computed(() => data.value === undefined && !error.value);

    /**
     * Always forced: swrv would otherwise drop a refetch requested inside its
     * deduping window, right after a mutation.
     */
    const refresh = () => mutate(undefined, {forceRevalidate: true});

    /**
     * Live refresh: a change made on another workstation, or a patient booking
     * online, must show up without waiting for the polling tick.
     */
    const liveRefresh = debounce(() => refresh(), LIVE_REFRESH_DEBOUNCE_MS);

    APPOINTMENT_EVENTS.forEach((event) => useOrganizationSocketEvent(event, liveRefresh));

    onScopeDispose(() => liveRefresh.cancel());

    /**
     * swrv skips its polling tick while the error state is set, and only a
     * successful fetch clears that state — so an outage outliving its retry
     * window would freeze the agenda until a manual retry. Keep probing on
     * our own while the error lasts.
     */
    const errorRetry = useIntervalFn(refresh, REFRESH_INTERVAL_MS, {immediate: false});

    watch(error, (currentError) => {
        if (currentError) {
            errorRetry.resume();
        } else {
            errorRetry.pause();
        }
    });

    return {
        data,
        reminders,
        error,
        isLoading,
        refresh,
        organizationId
    };
};

export default useAgenda;
